import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  BillingEntityInputDto,
  CreateClientDto,
  UpdateClientDto,
  DeliveryAddressDto,
  VatStatus,
} from '@jalupro/shared';
import {
  CLIENT_SOURCES,
  ClientType,
  CommercialCondition,
  TaxIdStatus,
  UserRole,
  can,
  classifySpanishTaxId,
  isSpanishTerritory,
  taxIdMatchesType,
  taxIdStatusFor,
} from '@jalupro/shared';
import { api, useMutation } from '../../api';
import type { ClientView, UserView } from '../../api';
import { useAuth } from '../../auth';
import { LoadingState, ErrorState } from '../../components';
import { useT, useLang, type TFunc } from '../../i18n';
import dict from './ClientForm.i18n';
import {
  sortedCountries,
  DEFAULT_COUNTRY_CODE,
  DIAL_CODES,
  splitPhone,
  countryFlag,
} from '../../lib/countries';
import { loadGoogleMaps, mapAddressComponents, type AddressParts } from '../../lib/googleMaps';

/**
 * Agent · Client form — a full-width page used for both create (no clientId) and
 * edit (with clientId). Routes: /agent/clients/new and /agent/clients/:id/edit.
 *
 * Delivery addresses:
 *  - CREATE, "same as billing" checked (default): the list editor is hidden and
 *    the DTO omits `deliveryAddresses` so the server seeds delivery[0] = billing.
 *  - CREATE, unchecked: the managed list editor shows, requiring >= 1 valid entry.
 *  - EDIT: the managed list is always shown, prefilled from client.deliveryAddresses
 *    (each entry keeps its `id`); add / remove / edit inline.
 */

// ── Local editable shapes ──────────────────────────────────────────────────────

interface AddressFields {
  line1: string;
  line2: string;
  city: string;
  province: string;
  postalCode: string;
  countryCode: string;
}

interface DeliveryEntry extends AddressFields {
  /** Preserved on edit so the server can match existing rows. */
  id?: string;
  label: string;
  /** The person the carrier asks for at this address. */
  contactName: string;
  /** The phone the carrier calls for this delivery (international format). */
  contactPhone: string;
}

/** An additional billing entity (legal entity with its own tax number). */
interface BillingEntityEntry {
  id?: string;
  legalName: string;
  tradeName: string;
  cifVat: string;
  vatNumber: string;
  billing: AddressFields;
}

interface FormState {
  company: string;
  tradeName: string;
  type: ClientType;
  cifVat: string;
  vatNumber: string;
  email: string;
  /** ISO-2 country of the phone's dial code (the select). */
  phoneCountry: string;
  /** National part of the phone number (without the dial code). */
  phoneNumber: string;
  contactFirstName: string;
  contactLastName: string;
  clinicDoctorName: string;
  collegiateNumber: string;
  billing: AddressFields;
  sourceChannel: string;
  commercialCondition: CommercialCondition;
  /** Language of the messages sent to the client ('' = not chosen). */
  language: string;
  sepaEligible: boolean;
}

interface ClientFormProps {
  clientId?: string;
  onDone: (saved: ClientView) => void;
  onCancel: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** National phone part: digits with common separators; 6–14 digits total. */
function phoneNumberValid(value: string): boolean {
  if (!/^[()\-\s.\d]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 6 && digits.length <= 14;
}

function emptyAddress(): AddressFields {
  return { line1: '', line2: '', city: '', province: '', postalCode: '', countryCode: DEFAULT_COUNTRY_CODE };
}

function emptyDelivery(): DeliveryEntry {
  return { ...emptyAddress(), label: '', contactName: '', contactPhone: '' };
}

function emptyBillingEntity(): BillingEntityEntry {
  return { legalName: '', tradeName: '', cifVat: '', vatNumber: '', billing: emptyAddress() };
}

function addressFromDto(a: { line1: string; line2?: string; city: string; province?: string; postalCode: string; countryCode: string }): AddressFields {
  return {
    line1: a.line1 ?? '',
    line2: a.line2 ?? '',
    city: a.city ?? '',
    province: a.province ?? '',
    postalCode: a.postalCode ?? '',
    countryCode: a.countryCode ?? DEFAULT_COUNTRY_CODE,
  };
}

function deliveryFromDto(d: DeliveryAddressDto): DeliveryEntry {
  return {
    ...addressFromDto(d),
    ...(d.id ? { id: d.id } : {}),
    label: d.label ?? '',
    contactName: d.contactName ?? '',
    contactPhone: d.contactPhone ?? '',
  };
}

/** Trimmed value or undefined when empty. */
function opt(value: string): string | undefined {
  const v = value.trim();
  return v ? v : undefined;
}

/** Build an AddressDto-shaped object from editable fields (assumes validated). */
function buildAddress(a: AddressFields) {
  return {
    line1: a.line1.trim(),
    ...(opt(a.line2) ? { line2: opt(a.line2) } : {}),
    city: a.city.trim(),
    ...(opt(a.province) ? { province: opt(a.province) } : {}),
    postalCode: a.postalCode.trim(),
    countryCode: a.countryCode.trim().toUpperCase(),
  };
}

function buildDelivery(d: DeliveryEntry): DeliveryAddressDto {
  return {
    ...buildAddress(d),
    ...(d.id ? { id: d.id } : {}),
    ...(opt(d.label) ? { label: opt(d.label) } : {}),
    ...(opt(d.contactName) ? { contactName: opt(d.contactName) } : {}),
    ...(opt(d.contactPhone) ? { contactPhone: opt(d.contactPhone) } : {}),
  };
}

function buildBillingEntity(e: BillingEntityEntry): BillingEntityInputDto {
  return {
    ...(e.id ? { id: e.id } : {}),
    legalName: e.legalName.trim(),
    ...(opt(e.tradeName) ? { tradeName: opt(e.tradeName) } : {}),
    cifVat: e.cifVat.trim(),
    ...(opt(e.vatNumber) ? { vatNumber: opt(e.vatNumber) } : {}),
    billingAddress: buildAddress(e.billing),
  };
}

function billingEntityValid(e: BillingEntityEntry): boolean {
  return Boolean(e.legalName.trim()) && addressValid(e.billing);
}

function addressValid(a: AddressFields): boolean {
  return Boolean(
    a.line1.trim() &&
      a.city.trim() &&
      a.postalCode.trim() &&
      a.countryCode.trim().length === 2,
  );
}

/** Stable signature of the editable form state — used to detect unsaved changes. */
function snapshotOf(
  form: FormState,
  deliveries: DeliveryEntry[],
  ownerAgentId: string,
  entities: BillingEntityEntry[] = [],
): string {
  return JSON.stringify({ form, deliveries, ownerAgentId, entities });
}

// ── Component ───────────────────────────────────────────────────────────────

export default function ClientForm({ clientId, onDone, onCancel }: ClientFormProps) {
  const t = useT(dict);
  const { lang } = useLang();
  const { role } = useAuth();
  const countries = useMemo(() => sortedCountries(lang), [lang]);
  const isEdit = Boolean(clientId);
  // Admin / super-admin may (re)assign the owning agent on an existing client.
  // Matrix: clients.reassign (Super Admin, Sales Manager).
  const canAssignAgent = !!role && can(role, 'clients.reassign');

  const create = useMutation(api.clients.create);
  const update = useMutation((dto: UpdateClientDto) => api.clients.update(clientId as string, dto));

  // Owner-agent reassignment (admin only). '' = unassigned.
  const [ownerAgentId, setOwnerAgentId] = useState<string>('');
  const [agents, setAgents] = useState<UserView[]>([]);

  // Snapshot of the loaded record (edit) so Save can detect unsaved changes.
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [initialOwnerAgentId, setInitialOwnerAgentId] = useState<string>('');

  // Initial load (edit only).
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    company: '',
    tradeName: '',
    type: ClientType.COMPANY,
    cifVat: '',
    vatNumber: '',
    email: '',
    phoneCountry: DEFAULT_COUNTRY_CODE,
    phoneNumber: '',
    contactFirstName: '',
    contactLastName: '',
    clinicDoctorName: '',
    collegiateNumber: '',
    billing: emptyAddress(),
    sourceChannel: '',
    commercialCondition: CommercialCondition.ESTANDAR,
    language: '',
    sepaEligible: false,
  });
  // SEPA eligibility is the Super Admin's field only.
  const isSuperAdmin = role === UserRole.SUPER_ADMIN;

  // On create, default "same as billing" ON and hide the editor.
  const [sameAsBilling, setSameAsBilling] = useState<boolean>(!isEdit);
  const [deliveries, setDeliveries] = useState<DeliveryEntry[]>([]);
  // Additional billing entities (the default one is the top-level fiscal block).
  const [billingEntities, setBillingEntities] = useState<BillingEntityEntry[]>([]);
  const checkDuplicates = useMutation(api.clients.checkDuplicates);

  const [showErrors, setShowErrors] = useState(false);

  // On-demand VIES VAT validation result (form "Validate" button / null = not run yet).
  const [vatCheck, setVatCheck] = useState<{
    status: VatStatus;
    name: string | null;
    address: string | null;
  } | null>(null);
  const validateVat = useMutation(api.clients.validateVat);

  useEffect(() => {
    let active = true;
    if (!clientId) return;
    setLoading(true);
    setLoadError(null);
    api.clients
      .get(clientId)
      .then((c) => {
        if (!active) return;
        const phone = splitPhone(c.phone ?? '');
        const loadedForm: FormState = {
          company: c.company ?? '',
          tradeName: c.tradeName ?? '',
          type: c.type ?? ClientType.COMPANY,
          cifVat: c.cifVat ?? '',
          vatNumber: c.vatNumber ?? '',
          email: c.emails[0] ?? '',
          phoneCountry: phone.countryCode,
          phoneNumber: phone.number,
          contactFirstName: c.contactFirstName ?? '',
          contactLastName: c.contactLastName ?? '',
          clinicDoctorName: c.clinicDoctorName ?? '',
          collegiateNumber: c.collegiateNumber ?? '',
          billing: c.billingAddress ? addressFromDto(c.billingAddress) : emptyAddress(),
          sourceChannel: c.sourceChannel ?? '',
          commercialCondition: c.commercialCondition ?? CommercialCondition.ESTANDAR,
          language: c.language ?? '',
          sepaEligible: c.sepaEligible ?? false,
        };
        const list = (c.deliveryAddresses ?? []).map(deliveryFromDto);
        const loadedList = list.length > 0 ? list : [emptyDelivery()];
        const loadedOwner = c.ownerAgentId ?? '';
        const loadedEntities: BillingEntityEntry[] = (c.billingEntities ?? [])
          .filter((e) => !e.isDefault)
          .map((e) => ({
            id: e.id,
            legalName: e.legalName,
            tradeName: e.tradeName ?? '',
            cifVat: e.cifVat ?? '',
            vatNumber: e.vatNumber ?? '',
            billing: e.billingAddress ? addressFromDto(e.billingAddress) : emptyAddress(),
          }));
        setForm(loadedForm);
        setDeliveries(loadedList);
        setBillingEntities(loadedEntities);
        // Show the stored VAT status as the initial badge.
        if (c.vatNumber) {
          setVatCheck({
            status: c.vatStatus,
            name: c.vatRegisteredName ?? null,
            address: c.vatRegisteredAddress ?? null,
          });
        }
        setOwnerAgentId(loadedOwner);
        setInitialOwnerAgentId(loadedOwner);
        setInitialSnapshot(snapshotOf(loadedForm, loadedList, loadedOwner, loadedEntities));
        setSameAsBilling(false); // edit always shows the managed list
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setLoadError(err instanceof Error ? err.message : t('loadError'));
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Load the agent roster for the owner-agent selector (admin only).
  useEffect(() => {
    if (!canAssignAgent) return;
    let active = true;
    api.agents
      .list({ page: 1, pageSize: 100 })
      .then((res) => {
        if (active) setAgents(res.items as UserView[]);
      })
      .catch(() => {
        /* non-fatal: the selector just stays empty */
      });
    return () => {
      active = false;
    };
  }, [canAssignAgent]);

  // When the user unchecks "same as billing" on create with no rows yet, seed one.
  function onToggleSame(checked: boolean) {
    setSameAsBilling(checked);
    if (!checked && deliveries.length === 0) {
      setDeliveries([emptyDelivery()]);
    }
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function doValidateVat() {
    const v = form.vatNumber.trim();
    if (!v) return;
    try {
      // Pass the billing country so a VAT entered without a prefix (e.g. "B85810182")
      // is validated against that country (→ ESB85810182).
      const r = await validateVat.mutate(v, form.billing.countryCode || undefined);
      setVatCheck(r);
      // Reflect the normalised (prefixed) form back into the field.
      if (r.normalized) setField('vatNumber', r.normalized);
    } catch {
      /* validateVat.error is surfaced in the field */
    }
  }

  function setBilling<K extends keyof AddressFields>(key: K, value: string) {
    setForm((f) => ({ ...f, billing: { ...f.billing, [key]: value } }));
  }

  /**
   * PERSON: on leaving the name field, auto-fill First/Last name from the person name
   * (first word → first name, the rest → last name). Never overwrites user input.
   */
  function autoSplitPersonName() {
    if (form.type !== ClientType.PERSON) return;
    const name = form.company.trim();
    if (!name) return;
    if (form.contactFirstName.trim() || form.contactLastName.trim()) return;
    const [first, ...rest] = name.split(/\s+/);
    setForm((f) => ({ ...f, contactFirstName: first, contactLastName: rest.join(' ') }));
  }

  function setDelivery(index: number, key: keyof DeliveryEntry, value: string) {
    setDeliveries((list) =>
      list.map((d, i) => (i === index ? { ...d, [key]: value } : d)),
    );
  }

  function addDelivery() {
    setDeliveries((list) => [...list, emptyDelivery()]);
  }

  function removeDelivery(index: number) {
    if (!window.confirm(t('confirmRemoveDelivery'))) return;
    setDeliveries((list) => list.filter((_, i) => i !== index));
  }

  function setEntity(index: number, patch: Partial<BillingEntityEntry>) {
    setBillingEntities((list) => list.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }
  function setEntityBilling(index: number, key: keyof AddressFields, value: string) {
    setBillingEntities((list) =>
      list.map((e, i) => (i === index ? { ...e, billing: { ...e.billing, [key]: value } } : e)),
    );
  }

  /** Display name for an agent id (or "Unassigned" for the empty value). */
  function agentLabel(id: string): string {
    if (!id) return t('ownerAgentUnassigned');
    return agents.find((a) => a.id === id)?.fullName ?? id;
  }

  // ── Validation ──────────────────────────────────────────────────────────────
  const managingDeliveries = isEdit || !sameAsBilling;

  const emailError = !form.email.trim()
    ? t('errRequired')
    : !EMAIL_RE.test(form.email.trim())
      ? t('errEmail')
      : null;
  const phoneError = !form.phoneNumber.trim()
    ? t('errRequired')
    : !phoneNumberValid(form.phoneNumber.trim())
      ? t('errPhone')
      : null;
  /** Full phone as stored: dial code + national number ("+34 600111222"). */
  const fullPhone = `${DIAL_CODES[form.phoneCountry] ?? ''} ${form.phoneNumber.trim()}`.trim();

  // The owner agent is mandatory: staff must pick one on create and cannot unassign on edit.
  const needsOwnerAgent = canAssignAgent;
  const ownerAgentError = needsOwnerAgent && !ownerAgentId ? t('errRequired') : null;

  // A confirmed-INVALID VAT no longer blocks saving: the client is blocked for orders
  // and listed for correction instead (the badge below says so).
  const vatBlocked = false;
  // The label depends on the billing country: NIF/CIF in Spain, "National number" elsewhere.
  const billingIsSpain = isSpanishTerritory(form.billing.countryCode);
  // In Spain the client type must match the tax number (CIF → company, NIF / NIE → person).
  const taxKind = billingIsSpain && form.cifVat.trim() ? classifySpanishTaxId(form.cifVat).kind : null;
  const taxTypeError = taxKind && !taxIdMatchesType(taxKind, form.type) ? t('errTaxType') : null;
  // Derived tax-number status of the default billing entity (what blocks orders).
  const taxStatus = taxIdStatusFor({
    cifVat: form.cifVat,
    countryCode: form.billing.countryCode,
    vatNumber: form.vatNumber,
    vatStatus: vatCheck?.status ?? null,
  });
  const taxStatusKey: Record<TaxIdStatus, string> = {
    VALID: 'taxValid',
    INVALID: 'taxInvalid',
    MISSING: 'taxMissing',
    UNVERIFIED: 'taxUnverified',
  };
  const showNoVatHint =
    form.type === ClientType.COMPANY && !billingIsSpain && !form.vatNumber.trim();

  // Source / channel is mandatory for new clients (existing ones may still lack it).
  const sourceError = !isEdit && !form.sourceChannel ? t('errRequired') : null;

  const canSubmit =
    Boolean(form.company.trim()) &&
    !sourceError &&
    !emailError &&
    !phoneError &&
    !ownerAgentError &&
    !taxTypeError &&
    !vatBlocked &&
    addressValid(form.billing) &&
    billingEntities.every(billingEntityValid) &&
    (!managingDeliveries || (deliveries.length > 0 && deliveries.every(addressValid)));

  const saving = create.loading || update.loading;
  const submitError = create.error ?? update.error;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setShowErrors(true);
    if (!canSubmit) return;

    // Duplicate pre-check: another client with the same e-mail or phone is a warning,
    // never a refusal (the tax number is the only key).
    try {
      const dup = await checkDuplicates.mutate({
        email: form.email.trim(),
        phone: fullPhone,
        ...(clientId ? { excludeId: clientId } : {}),
      });
      if (dup.emailCount > 0 || dup.phoneCount > 0) {
        const field = dup.emailCount > 0 ? t('duplicateFieldEmail') : t('duplicateFieldPhone');
        const count = dup.emailCount > 0 ? dup.emailCount : dup.phoneCount;
        if (!window.confirm(t('duplicateWarning', { field, count }))) return;
      }
    } catch {
      /* the check is advisory; saving proceeds */
    }

    const base = {
      company: form.company.trim(),
      // Trade name is companies-only; '' clears a stored value (incl. type switches).
      tradeName: form.type === ClientType.COMPANY ? form.tradeName.trim() : '',
      type: form.type,
      // Fiscal id is optional for both types; always sent — '' clears a stored value.
      cifVat: form.cifVat.trim(),
      // VAT is companies-only. Always send it for a COMPANY — including an empty string,
      // which clears a previously-stored number (otherwise an old INVALID VAT can't be
      // removed). Persons send nothing (the server clears it for PERSON anyway).
      ...(form.type === ClientType.COMPANY ? { vatNumber: form.vatNumber.trim() } : {}),
      emails: [form.email.trim()],
      // Mandatory (validated above), international format: dial code + national number.
      phone: fullPhone,
      ...(opt(form.contactFirstName) ? { contactFirstName: opt(form.contactFirstName) } : {}),
      ...(opt(form.contactLastName) ? { contactLastName: opt(form.contactLastName) } : {}),
      ...(opt(form.clinicDoctorName) ? { clinicDoctorName: opt(form.clinicDoctorName) } : {}),
      ...(opt(form.collegiateNumber) ? { collegiateNumber: opt(form.collegiateNumber) } : {}),
      billingAddress: buildAddress(form.billing),
      // Mandatory on create (validated above); an existing client may still lack it.
      sourceChannel: (form.sourceChannel || null) as CreateClientDto['sourceChannel'],
      commercialCondition: form.commercialCondition,
      language: (form.language || null) as CreateClientDto['language'],
      ...(isSuperAdmin ? { sepaEligible: form.sepaEligible } : {}),
      extraBillingEntities: billingEntities.map(buildBillingEntity),
    };

    const deliveryAddresses = managingDeliveries
      ? deliveries.map(buildDelivery)
      : undefined;

    if (isEdit) {
      // Confirm before saving when anything changed; call out an agent reassignment.
      const changed = snapshotOf(form, deliveries, ownerAgentId, billingEntities) !== initialSnapshot;
      const agentChanged = canAssignAgent && ownerAgentId !== initialOwnerAgentId;
      if (changed) {
        const message = agentChanged
          ? t('confirmReassign', { agent: agentLabel(ownerAgentId) })
          : t('confirmSave');
        if (!window.confirm(message)) return;
      }

      const dto: UpdateClientDto = {
        ...base,
        // On edit we always manage the list, so always send it.
        deliveryAddresses,
        // Owner-agent reassignment (the owner is mandatory, so always a real agent id).
        ...(canAssignAgent && ownerAgentId ? { ownerAgentId } : {}),
      };
      update
        .mutate(dto)
        .then((saved) => onDone(saved))
        .catch(() => {
          /* update.error surfaced below */
        });
      return;
    }

    const dto: CreateClientDto = {
      ...base,
      // Omit when "same as billing" so the server seeds delivery[0] = billing.
      ...(deliveryAddresses ? { deliveryAddresses } : {}),
      // Admins choose the owning agent on create (required by the UI).
      ...(canAssignAgent && ownerAgentId ? { ownerAgentId } : {}),
    };
    create
      .mutate(dto)
      .then((saved) => onDone(saved))
      .catch(() => {
        /* create.error surfaced below */
      });
  }

  return (
    <>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{isEdit ? t('editSubtitle') : t('createSubtitle')}</div>
          <div className="h1">{isEdit ? t('editTitle') : t('createTitle')}</div>
        </div>
        <div className="row gap-8">
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>
            {t('cancelBtn')}
          </button>
          <button
            type="submit"
            form="client-form"
            className="btn primary"
            disabled={saving || vatBlocked || (showErrors && !canSubmit)}
          >
            {saving ? t('savingBtn') : isEdit ? t('saveChangesBtn') : t('createClientBtn')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 22px', overflow: 'auto', flex: 1 }}>
        {loading ? (
          <LoadingState label={t('loading')} />
        ) : loadError ? (
          <ErrorState message={loadError} />
        ) : (
          <form id="client-form" onSubmit={(e) => void submit(e)} style={{ maxWidth: 760 }}>
            {submitError && (
              <div className="mb-12">
                <ErrorState message={submitError.message} />
              </div>
            )}

            {/* Owner agent — admin only; pinned to the top + tinted so it stands out
                (required on create, reassignable on edit). */}
            {canAssignAgent && (
              <div
                className="panel mb-16"
                style={{ padding: 18, background: 'var(--navy-bg)', borderColor: 'var(--navy-line)' }}
              >
                <div className="h3 mb-12" style={{ color: 'var(--s1)' }}>
                  {t('ownerAgentHeading')}
                </div>
                <Field label={t('ownerAgentLabel')} error={showErrors ? ownerAgentError : null}>
                  <div className="input" style={{ padding: 0, background: '#fff' }}>
                    <select
                      value={ownerAgentId}
                      onChange={(e) => setOwnerAgentId(e.target.value)}
                      style={{
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        font: 'inherit',
                        color: 'var(--ink)',
                        width: '100%',
                        padding: '6px 10px',
                      }}
                    >
                      <option value="">{t('ownerAgentSelect')}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.fullName} · {a.email}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
            )}

            <div className="panel" style={{ padding: 18 }}>
              <div className="col gap-12">
                {/* Client type — Company vs Person. Switching to Person clears the VAT. */}
                <Field label={t('fieldClientType')}>
                  <div className="row gap-6">
                    {[ClientType.COMPANY, ClientType.PERSON].map((ct) => {
                      const active = form.type === ct;
                      return (
                        <button
                          key={ct}
                          type="button"
                          className={`btn sm ${active ? 'primary' : ''}`}
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              type: ct,
                              ...(ct === ClientType.PERSON ? { vatNumber: '' } : {}),
                            }));
                            if (ct === ClientType.PERSON) setVatCheck(null);
                          }}
                        >
                          {ct === ClientType.COMPANY ? t('typeCompany') : t('typePerson')}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field
                  label={form.type === ClientType.PERSON ? t('fieldPersonName') : t('fieldCompany')}
                  error={showErrors && !form.company.trim() ? t('errRequired') : null}
                >
                  <TextInput
                    value={form.company}
                    onChange={(v) => setField('company', v)}
                    onBlur={autoSplitPersonName}
                    placeholder={
                      form.type === ClientType.PERSON
                        ? t('fieldPersonNamePlaceholder')
                        : t('fieldCompanyPlaceholder')
                    }
                  />
                </Field>
                <div className="row gap-8">
                  <div className="grow">
                    <Field label={t('fieldEmail')} error={showErrors ? emailError : null}>
                      <TextInput
                        value={form.email}
                        onChange={(v) => setField('email', v)}
                        placeholder={t('fieldEmailPlaceholder')}
                        type="email"
                        mono
                      />
                    </Field>
                  </div>
                  <div className="grow">
                    <Field label={t('fieldPhone')} error={showErrors ? phoneError : null}>
                      <div className="row gap-8">
                        <div style={{ width: 210, flexShrink: 0 }}>
                          <DialCodeSelect
                            value={form.phoneCountry}
                            onChange={(v) => setField('phoneCountry', v)}
                            countries={countries}
                            ariaLabel={t('fieldPhoneCountry')}
                          />
                        </div>
                        <div className="grow">
                          <TextInput
                            value={form.phoneNumber}
                            onChange={(v) => setField('phoneNumber', v)}
                            placeholder={t('fieldPhonePlaceholder')}
                            mono
                          />
                        </div>
                      </div>
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact person */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="h3 mb-12">{t('contactPersonHeading')}</div>
              <div className="col gap-12">
                <div className="row gap-8">
                  <div className="grow">
                    <Field label={t('fieldFirstName')}>
                      <TextInput
                        value={form.contactFirstName}
                        onChange={(v) => setField('contactFirstName', v)}
                        placeholder="María"
                      />
                    </Field>
                  </div>
                  <div className="grow">
                    <Field label={t('fieldLastName')}>
                      <TextInput
                        value={form.contactLastName}
                        onChange={(v) => setField('contactLastName', v)}
                        placeholder="García"
                      />
                    </Field>
                  </div>
                </div>
                <div className="row gap-8">
                  <div className="grow">
                    <Field label={t('fieldClinicDoctorName')}>
                      <TextInput
                        value={form.clinicDoctorName}
                        onChange={(v) => setField('clinicDoctorName', v)}
                        placeholder="Dr. Sole"
                      />
                    </Field>
                  </div>
                  <div className="grow">
                    <Field label={t('fieldCollegiateNumber')}>
                      <TextInput
                        value={form.collegiateNumber}
                        onChange={(v) => setField('collegiateNumber', v)}
                        placeholder="280012345"
                        mono
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile — source, commercial condition, language, SEPA */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="h3 mb-12">{t('profileHeading')}</div>
              <div className="col gap-12">
                <div className="row gap-8">
                  <div className="grow">
                    <Field label={t('fieldSource')} error={showErrors ? sourceError : null}>
                      <SelectInput value={form.sourceChannel} onChange={(v) => setField('sourceChannel', v)}>
                        <option value="">{t('sourceNone')}</option>
                        {CLIENT_SOURCES.map((src) => (
                          <option key={src} value={src}>
                            {t(`source_${src}`)}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                  </div>
                  <div className="grow">
                    <Field label={t('fieldCommercialCondition')}>
                      <SelectInput
                        value={form.commercialCondition}
                        onChange={(v) => setField('commercialCondition', v as CommercialCondition)}
                      >
                        {Object.values(CommercialCondition).map((cc) => (
                          <option key={cc} value={cc}>
                            {t(`condition_${cc}`)}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                  </div>
                </div>
                <div className="row gap-8">
                  <div className="grow">
                    <Field label={t('fieldLanguage')}>
                      <SelectInput value={form.language} onChange={(v) => setField('language', v)}>
                        <option value="">{t('languageNotSet')}</option>
                        <option value="es">Español</option>
                        <option value="en">English</option>
                      </SelectInput>
                    </Field>
                  </div>
                  {isSuperAdmin && (
                    <div className="grow">
                      <label className="col gap-4">
                        <span className="label">{t('fieldSepaEligible')}</span>
                        <label className="row gap-8 center" style={{ height: CONTROL_HEIGHT, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={form.sepaEligible}
                            onChange={(e) => setField('sepaEligible', e.target.checked)}
                          />
                          <span className="meta" style={{ fontSize: 13 }}>{t('sepaEligibleHint')}</span>
                        </label>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Billing address */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="h3 mb-12">{t('billingAddressHeading')}</div>
              <AddressEditor
                t={t}
                value={form.billing}
                onChange={setBilling}
                onAutofill={(a) =>
                  setForm((f) => ({ ...f, billing: { ...f.billing, ...a } }))
                }
                countries={countries}
                showErrors={showErrors}
              />
            </div>

            {/* Fiscal details — below the billing address because the fiscal-id label
                (NIF/CIF vs national number) follows the billing country. */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="h3 mb-12">{t('fiscalHeading')}</div>
              <div className="col gap-12">
                {form.type === ClientType.COMPANY && (
                  <Field label={t('fieldTradeName')}>
                    <TextInput
                      value={form.tradeName}
                      onChange={(v) => setField('tradeName', v)}
                      placeholder={t('fieldTradeNamePlaceholder')}
                    />
                  </Field>
                )}
                {/* Fiscal id — NIF/CIF in Spain, national (non-VAT) number elsewhere. */}
                <Field
                  label={billingIsSpain ? t('fieldCifVatEs') : t('fieldCifVatIntl')}
                  error={showErrors ? taxTypeError : null}
                >
                  <TextInput value={form.cifVat} onChange={(v) => setField('cifVat', v)} placeholder="B12345678" mono />
                  <span
                    className="label"
                    style={{ fontWeight: 400, color: taxStatus === 'VALID' ? 'var(--green)' : 'var(--red)' }}
                  >
                    {t(taxStatusKey[taxStatus])}
                  </span>
                </Field>
                {form.type === ClientType.COMPANY && (
                  <Field label={t('fieldVatNumber')}>
                    <div className="row gap-8 center">
                      <div className="grow">
                        <TextInput
                          value={form.vatNumber}
                          onChange={(v) => {
                            setField('vatNumber', v);
                            setVatCheck(null);
                          }}
                          onBlur={() => {
                            // Auto-validate on leaving the field (skip if already checked
                            // for the current value or a check is in flight).
                            if (form.vatNumber.trim() && !vatCheck && !validateVat.loading) {
                              void doValidateVat();
                            }
                          }}
                          placeholder="ESB12345678"
                          mono
                        />
                      </div>
                      <button
                        type="button"
                        className="btn sm"
                        disabled={validateVat.loading || !form.vatNumber.trim()}
                        onClick={() => void doValidateVat()}
                      >
                        {validateVat.loading ? t('vatChecking') : t('vatValidateBtn')}
                      </button>
                    </div>
                    {vatCheck && <VatBadge status={vatCheck.status} name={vatCheck.name} t={t} />}
                    {showNoVatHint && (
                      <span className="label" style={{ fontWeight: 400 }}>
                        {t('vatNoVatHint')}
                      </span>
                    )}
                    {validateVat.error && (
                      <span className="label" style={{ color: 'var(--red)', fontWeight: 400 }}>
                        {validateVat.error.message}
                      </span>
                    )}
                  </Field>
                )}
              </div>
            </div>

            {/* Other billing entities — legal entities the proforma / invoice can be issued to */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="row between center mb-8">
                <div className="h3">{t('billingEntitiesHeading')}</div>
              </div>
              <div className="col gap-12">
                <div className="meta" style={{ fontSize: 13 }}>
                  {t('billingEntitiesHint')}
                </div>
                {billingEntities.map((e, i) => (
                  <div key={e.id ?? `new-entity-${i}`} className="panel" style={{ padding: 14, background: 'var(--card-2)' }}>
                    <div className="row between center mb-8">
                      <div className="label">{t('billingEntityN', { n: i + 1 })}</div>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={() => setBillingEntities((list) => list.filter((_, idx) => idx !== i))}
                      >
                        {t('removeBillingEntity')}
                      </button>
                    </div>
                    <div className="col gap-12">
                      <div className="row gap-8">
                        <div className="grow">
                          <Field label={t('fieldLegalName')} error={showErrors && !e.legalName.trim() ? t('errRequired') : null}>
                            <TextInput value={e.legalName} onChange={(v) => setEntity(i, { legalName: v })} placeholder="Clínica Norte SL" />
                          </Field>
                        </div>
                        <div className="grow">
                          <Field label={t('fieldTradeName')}>
                            <TextInput value={e.tradeName} onChange={(v) => setEntity(i, { tradeName: v })} />
                          </Field>
                        </div>
                      </div>
                      <div className="row gap-8">
                        <div className="grow">
                          <Field label={isSpanishTerritory(e.billing.countryCode) ? t('fieldCifVatEs') : t('fieldCifVatIntl')}>
                            <TextInput value={e.cifVat} onChange={(v) => setEntity(i, { cifVat: v })} placeholder="B12345678" mono />
                            <span
                              className="label"
                              style={{
                                fontWeight: 400,
                                color:
                                  taxIdStatusFor({ cifVat: e.cifVat, countryCode: e.billing.countryCode, vatNumber: e.vatNumber }) === 'VALID'
                                    ? 'var(--green)'
                                    : 'var(--red)',
                              }}
                            >
                              {t(taxStatusKey[taxIdStatusFor({ cifVat: e.cifVat, countryCode: e.billing.countryCode, vatNumber: e.vatNumber })])}
                            </span>
                          </Field>
                        </div>
                        <div className="grow">
                          <Field label={t('fieldVatNumber')}>
                            <TextInput value={e.vatNumber} onChange={(v) => setEntity(i, { vatNumber: v })} placeholder="ESB12345678" mono />
                          </Field>
                        </div>
                      </div>
                      <AddressEditor
                        t={t}
                        value={e.billing}
                        onChange={(key, v) => setEntityBilling(i, key, v)}
                        countries={countries}
                        showErrors={showErrors}
                      />
                    </div>
                  </div>
                ))}
                <div>
                  <button type="button" className="btn" onClick={() => setBillingEntities((list) => [...list, emptyBillingEntity()])}>
                    {t('addBillingEntity')}
                  </button>
                </div>
              </div>
            </div>

            {/* Delivery addresses */}
            <div className="panel mt-16" style={{ padding: 18 }}>
              <div className="row between center mb-8">
                <div className="h3">{t('deliveryAddressesHeading')}</div>
              </div>
              {!isEdit && (
                <label className="row gap-8 center mb-12" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={(e) => onToggleSame(e.target.checked)}
                  />
                  <span className="meta" style={{ fontSize: 13 }}>
                    {t('deliverySameAsBilling')}
                  </span>
                </label>
              )}

              {managingDeliveries && (
                <div className="col gap-12">
                  <div className="meta" style={{ fontSize: 13 }}>
                    {t('deliveryHint')}
                  </div>
                  {showErrors && deliveries.length === 0 && (
                    <div className="label" style={{ color: 'var(--red)' }}>
                      {t('errAtLeastOneDelivery')}
                    </div>
                  )}
                  {deliveries.map((d, i) => (
                    <div
                      key={d.id ?? `new-${i}`}
                      className="panel"
                      style={{ padding: 14, background: 'var(--card-2)' }}
                    >
                      <div className="row between center mb-8">
                        <div className="label">{t('deliveryAddressN', { n: i + 1 })}</div>
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => removeDelivery(i)}
                        >
                          {t('removeDeliveryAddress')}
                        </button>
                      </div>
                      <div className="col gap-12">
                        <Field label={t('fieldLabel')}>
                          <TextInput
                            value={d.label}
                            onChange={(v) => setDelivery(i, 'label', v)}
                            placeholder={t('fieldLabelPlaceholder')}
                          />
                        </Field>
                        <div className="row gap-8">
                          <div className="grow">
                            <Field label={t('fieldDeliveryContactName')}>
                              <TextInput
                                value={d.contactName}
                                onChange={(v) => setDelivery(i, 'contactName', v)}
                                placeholder="María García"
                              />
                            </Field>
                          </div>
                          <div className="grow">
                            <Field label={t('fieldDeliveryContactPhone')}>
                              <TextInput
                                value={d.contactPhone}
                                onChange={(v) => setDelivery(i, 'contactPhone', v)}
                                placeholder="+34 600 000 000"
                                mono
                              />
                            </Field>
                          </div>
                        </div>
                        <AddressEditor
                          t={t}
                          value={d}
                          onChange={(key, v) => setDelivery(i, key, v)}
                          onAutofill={(a) =>
                            setDeliveries((list) =>
                              list.map((entry, idx) => (idx === i ? { ...entry, ...a } : entry)),
                            )
                          }
                          countries={countries}
                          showErrors={showErrors}
                        />
                      </div>
                    </div>
                  ))}
                  <div>
                    <button type="button" className="btn" onClick={addDelivery}>
                      {t('addDeliveryAddress')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions (mirror topbar for long pages) */}
            <div className="row gap-8 end mt-16">
              <button type="button" className="btn" onClick={onCancel} disabled={saving}>
                {t('cancelBtn')}
              </button>
              <button type="submit" className="btn primary" disabled={saving || vatBlocked}>
                {saving ? t('savingBtn') : isEdit ? t('saveChangesBtn') : t('createClientBtn')}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Google Places address autocomplete. Lazily loads Maps (Places) using the key from
 * GET /config; if no key is configured (or load fails) it renders nothing and manual
 * entry is unaffected. On pick, fills the structured address fields via `onPick`.
 */
function AddressAutocomplete({
  label,
  placeholder,
  onPick,
}: {
  label: string;
  placeholder: string;
  onPick: (a: AddressParts) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  // Keep the latest onPick without re-running the mount effect (parent passes an inline fn).
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let el: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let handler: ((ev: any) => void) | null = null;
    void (async () => {
      const maps = await loadGoogleMaps();
      if (cancelled || !maps || !containerRef.current) return;
      try {
        const places = maps.places ?? (await maps.importLibrary?.('places'));
        const Ctor = places?.PlaceAutocompleteElement;
        if (!Ctor) return;
        el = new Ctor();
        try {
          el.placeholder = placeholder;
        } catch {
          /* placeholder may be read-only on some versions */
        }
        el.style.width = '100%';
        // Force the light theme so the widget matches the app (it otherwise follows
        // the OS dark mode and renders as a dark box).
        el.style.colorScheme = 'light';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handler = async (ev: any) => {
          try {
            const place = ev?.placePrediction?.toPlace?.() ?? ev?.place ?? null;
            if (!place) return;
            if (place.fetchFields) await place.fetchFields({ fields: ['addressComponents'] });
            const comps = place.addressComponents ?? place.address_components ?? [];
            onPickRef.current(mapAddressComponents(comps));
            try {
              el.value = '';
            } catch {
              /* ignore */
            }
          } catch {
            /* ignore a single bad selection */
          }
        };
        // Event name drifted across Places versions — listen for both.
        el.addEventListener('gmp-select', handler);
        el.addEventListener('gmp-placeselect', handler);
        containerRef.current.appendChild(el);
        if (!cancelled) setReady(true);
      } catch {
        /* feature stays hidden on any init failure */
      }
    })();
    return () => {
      cancelled = true;
      if (el && handler) {
        el.removeEventListener('gmp-select', handler);
        el.removeEventListener('gmp-placeselect', handler);
      }
      if (el?.remove) el.remove();
    };
  }, [placeholder]);

  // Nothing renders until the widget mounts (no key / load failure → hidden).
  return (
    <div style={{ display: ready ? 'block' : 'none' }}>
      <span className="label">{label}</span>
      <div ref={containerRef} style={{ marginTop: 4 }} />
    </div>
  );
}

function AddressEditor({
  t,
  value,
  onChange,
  onAutofill,
  countries,
  showErrors,
}: {
  t: TFunc;
  value: AddressFields;
  onChange: (key: keyof AddressFields, v: string) => void;
  onAutofill?: (a: AddressParts) => void;
  countries: ReadonlyArray<{ code: string; name: string }>;
  showErrors: boolean;
}) {
  return (
    <div className="col gap-12">
      {onAutofill && (
        <AddressAutocomplete
          label={t('fieldAddressSearch')}
          placeholder={t('fieldAddressSearchPlaceholder')}
          onPick={onAutofill}
        />
      )}
      <Field label={t('fieldAddress')} error={showErrors && !value.line1.trim() ? t('errRequired') : null}>
        <TextInput value={value.line1} onChange={(v) => onChange('line1', v)} placeholder="Calle Mayor 1" />
      </Field>
      <Field label={t('fieldAddress2')}>
        <TextInput value={value.line2} onChange={(v) => onChange('line2', v)} placeholder="2º B" />
      </Field>
      <div className="row gap-8">
        <div className="grow">
          <Field label={t('fieldCity')} error={showErrors && !value.city.trim() ? t('errRequired') : null}>
            <TextInput value={value.city} onChange={(v) => onChange('city', v)} placeholder="Madrid" />
          </Field>
        </div>
        <div className="grow">
          <Field label={t('fieldProvince')}>
            <TextInput value={value.province} onChange={(v) => onChange('province', v)} placeholder="Madrid" />
          </Field>
        </div>
      </div>
      <div className="row gap-8">
        <div style={{ width: 160 }}>
          <Field
            label={t('fieldPostalCode')}
            error={showErrors && !value.postalCode.trim() ? t('errRequired') : null}
          >
            <TextInput value={value.postalCode} onChange={(v) => onChange('postalCode', v)} placeholder="28013" mono />
          </Field>
        </div>
        <div className="grow">
          <Field
            label={t('fieldCountry')}
            error={showErrors && value.countryCode.trim().length !== 2 ? t('errCountry') : null}
          >
            <CountrySelect
              value={value.countryCode}
              onChange={(v) => onChange('countryCode', v)}
              countries={countries}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

/** VIES VAT status pill (+ registered name when the member state discloses it). */
function VatBadge({
  status,
  name,
  t,
}: {
  status: VatStatus;
  name: string | null;
  t: TFunc;
}) {
  const tone: Record<VatStatus, string> = {
    VALID: 'green',
    INVALID: 'red',
    UNAVAILABLE: 'amber',
    NOT_APPLICABLE: '',
    UNVERIFIED: '',
  };
  const label: Record<VatStatus, string> = {
    VALID: t('vatValid'),
    INVALID: t('vatInvalid'),
    UNAVAILABLE: t('vatUnavailable'),
    NOT_APPLICABLE: t('vatNotApplicable'),
    UNVERIFIED: t('vatUnverified'),
  };
  return (
    <div className="row center gap-8" style={{ marginTop: 4 }}>
      <span className={`pill ${tone[status]} dot`}>{label[status]}</span>
      {name && (
        <span className="meta" style={{ fontSize: 12 }}>
          {name}
        </span>
      )}
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <label className="col gap-4">
      <span className="label">{label}</span>
      {children}
      {error ? (
        <span className="label" style={{ color: 'var(--red)', fontWeight: 400 }}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  onBlur,
  placeholder,
  type = 'text',
  mono = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: string;
  mono?: boolean;
}) {
  return (
    <div className="input" style={{ padding: 0, height: CONTROL_HEIGHT }}>
      <input
        className={mono ? 'mono' : undefined}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          color: 'var(--ink)',
          width: '100%',
          height: '100%',
          padding: '6px 10px',
        }}
      />
    </div>
  );
}

/** Uniform control height so inputs and selects in one row line up exactly. */
const CONTROL_HEIGHT = 34;

function SelectInput({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="input" style={{ padding: 0, height: CONTROL_HEIGHT }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          color: 'var(--ink)',
          width: '100%',
          height: '100%',
          padding: '6px 10px',
        }}
      >
        {children}
      </select>
    </div>
  );
}

/** Phone dial-code select: "+34 · Spain" per country, value = ISO-2 code. */
function DialCodeSelect({
  value,
  onChange,
  countries,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  countries: ReadonlyArray<{ code: string; name: string }>;
  ariaLabel: string;
}) {
  return (
    <div className="input" style={{ padding: 0, height: CONTROL_HEIGHT }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          color: 'var(--ink)',
          width: '100%',
          height: '100%',
          padding: '6px 10px',
        }}
      >
        {countries.map((c) => (
          <option key={c.code} value={c.code}>
            {countryFlag(c.code)} {DIAL_CODES[c.code] ?? ''} · {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function CountrySelect({
  value,
  onChange,
  countries,
}: {
  value: string;
  onChange: (v: string) => void;
  countries: ReadonlyArray<{ code: string; name: string }>;
}) {
  return (
    <div className="input" style={{ padding: 0, height: CONTROL_HEIGHT }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          color: 'var(--ink)',
          width: '100%',
          height: '100%',
          padding: '6px 10px',
        }}
      >
        {countries.map((c) => (
          <option key={c.code} value={c.code}>
            {countryFlag(c.code)} {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
